import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { SmallLoader } from "../buttons/Buttons";
import { sortedArrayByName } from "app/helpers/helperFunction";
import { Dropdown } from "bootstrap";

interface subDropDown {
  value: string | number;
  name: string;
}

interface option {
  value: string | number | undefined;
  name: string | undefined;
  subDropDown?: Array<subDropDown>;
}

interface props {
  placeholder: string;
  options: Array<option>;
  setFieldValue?: Function;
  name?: string;
  apiCallDispatch?: Function;
  defaultValue?: option;
  className: string;
  currentValue?: {
    value: string | number | undefined | null;
    name?: string | undefined;
  };
  disabled?: boolean;
  showSearch?: boolean;
  displayLoader?: boolean;
  setFieldValueDefault?: boolean;
  prefix?: string;
  onChange?: (e: any) => void;
  id?: string;
  showSorted?: boolean;
}

interface selected {
  selectedValue: number | string | null | undefined;
  selectedName: string | null | undefined;
}

const DropDown: FC<props> = ({
  disabled,
  currentValue,
  apiCallDispatch,
  placeholder,
  options = [],
  setFieldValue,
  name,
  defaultValue,
  className,
  showSearch,
  displayLoader,
  setFieldValueDefault,
  prefix,
  id,
  onChange,
  showSorted = true,
}) => {

  options = showSorted ? sortedArrayByName([...options]) : options;
  const [selected, setSelected] = useState<selected>({
    selectedValue: defaultValue?.value || currentValue?.value,
    selectedName: defaultValue?.name || currentValue?.name,
  });
  const [search, setSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const handleOptionClick = (
    optionValue: string | number | undefined | null,
    optionName: string | null | undefined
  ) => {
    setSelected({
      selectedValue: optionValue,
      selectedName: optionName,
    });
    setFieldValue && setFieldValue(name, optionValue);
    apiCallDispatch && apiCallDispatch(optionValue, optionName);

    // Call onChange if it exists
    if (onChange) {
      onChange({ value: optionValue, name: optionName });
    }
  };

  useEffect(() => {
    if (setFieldValueDefault) {
      setFieldValue && currentValue && setFieldValue(name, currentValue?.value);
      setFieldValue && defaultValue && setFieldValue(name, defaultValue?.value);
    }
  }, []);

  // Focus the search input
  useEffect(() => {
    const dropdownButton = document.getElementById(
      id ? id : `dropdown-button-${name}`
    );

    if (dropdownButton) {
      const handleDropdownShown = () => {
        setTimeout(() => {
          searchInputRef.current?.focus();
          // if (showSearch) {
          //   searchInputRef.current?.focus();
          // } else {
          //   // Find first selectable item
          //   const firstItem = document.querySelector(
          //     `#custom-dropdown-${name} .dropdown-item`
          //   ) as HTMLLIElement;
          //   if (firstItem) {
          //     firstItem.classList.add("dropdown-option-selected");
          //     setSelectedIndex(0);
          //     firstItem.focus(); // Ensure focus is on first item
          //   }
          // }
        }, 100);
      };

      dropdownButton.addEventListener("click", handleDropdownShown);
      dropdownButton.addEventListener("shown.bs.dropdown", handleDropdownShown);

      return () => {
        dropdownButton.removeEventListener("click", handleDropdownShown);
        dropdownButton.removeEventListener(
          "shown.bs.dropdown",
          handleDropdownShown
        );
      };
    }
  }, [id, name, showSearch]);

  // Update className for active item based on selectedIndex
  const getItemClassName = (index: number) => {
    return index === selectedIndex
      ? "dropdown-item dropdown-option-selected"
      : "dropdown-item";
  };

  // const handleOptionClick = (e: any) => {
  //   const listItem = (e.target as HTMLElement).closest("li");
  //   if (listItem && listItem.dataset.id) {
  //     setSelected({
  //       selectedName: listItem.textContent,
  //       selectedValue: listItem.dataset.id,
  //     });
  //     setFieldValue && setFieldValue(name, listItem.dataset.id);
  //     apiCallDispatch && apiCallDispatch(listItem.dataset.id);
  //   }
  // };

  const searchFun = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    let input = document.getElementById(`myInput-${name}`) as HTMLInputElement;
    let filter = input.value.toUpperCase();
    let div = document.getElementById(`custom-dropdown-${name}`) as HTMLElement;
    let a = div.getElementsByTagName("li") as HTMLCollectionOf<HTMLLIElement>;
    for (let i = 0; i < a.length; i++) {
      let txtValue = a[i].textContent || a[i].innerText;
      if (txtValue.toUpperCase().indexOf(filter) > -1) {
        a[i].classList.remove("hidden-imp");
      } else {
        a[i].classList.add("hidden-imp");
      }
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "ArrowDown") {
      setSelectedIndex((prevIndex) => {
        // Get all visible dropdown items (not hidden by search)
        const visibleItems = document.querySelectorAll(
          `#custom-dropdown-${name} .dropdown-item:not(.hidden-imp)`
        );
        
        // If no visible items, return current index
        if (visibleItems.length === 0) return prevIndex;
        
        // Find the current visible index
        let currentVisibleIndex = -1;
        if (prevIndex !== null) {
          for (let i = 0; i < visibleItems.length; i++) {
            if (visibleItems[i].classList.contains("dropdown-option-selected")) {
              currentVisibleIndex = i;
              break;
            }
          }
        }
        
        // Move to next visible item
        const nextVisibleIndex = currentVisibleIndex + 1;
        if (nextVisibleIndex < visibleItems.length) {
          // Remove selected class from all items
          visibleItems.forEach(item => item.classList.remove("dropdown-option-selected"));
          
          // Add selected class to next item
          const nextItem = visibleItems[nextVisibleIndex] as HTMLLIElement;
          nextItem.classList.add("dropdown-option-selected");
          
          // Find the actual index in the original options array
          const nextItemId = nextItem.dataset.id;
          const actualIndex = options.findIndex(opt => String(opt.value) === nextItemId);
          
          // Scroll the item into view
          nextItem.scrollIntoView({ block: "nearest" });
          
          return actualIndex;
        }
        return prevIndex;
      });
    } else if (e.key === "ArrowUp") {
      setSelectedIndex((prevIndex) => {
        // Get all visible dropdown items (not hidden by search)
        const visibleItems = document.querySelectorAll(
          `#custom-dropdown-${name} .dropdown-item:not(.hidden-imp)`
        );
        
        // If no visible items, return current index
        if (visibleItems.length === 0) return prevIndex;
        
        // Find the current visible index
        let currentVisibleIndex = -1;
        if (prevIndex !== null) {
          for (let i = 0; i < visibleItems.length; i++) {
            if (visibleItems[i].classList.contains("dropdown-option-selected")) {
              currentVisibleIndex = i;
              break;
            }
          }
        }
        
        // Move to previous visible item
        const prevVisibleIndex = currentVisibleIndex - 1;
        if (prevVisibleIndex >= 0) {
          // Remove selected class from all items
          visibleItems.forEach(item => item.classList.remove("dropdown-option-selected"));
          
          // Add selected class to previous item
          const prevItem = visibleItems[prevVisibleIndex] as HTMLLIElement;
          prevItem.classList.add("dropdown-option-selected");
          
          // Find the actual index in the original options array
          const prevItemId = prevItem.dataset.id;
          const actualIndex = options.findIndex(opt => String(opt.value) === prevItemId);
          
          // Scroll the item into view
          prevItem.scrollIntoView({ block: "nearest" });
          
          return actualIndex;
        }
        return prevIndex;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex !== null && options) {
        const selectedOption = options[selectedIndex];
        handleOptionClick(selectedOption.value, selectedOption.name);

        const dropdownButton = document.getElementById(
          id ? id : `dropdown-button-${name}`
        );
        if (dropdownButton) {
          const dropdownInstance =
            Dropdown.getInstance(dropdownButton) ||
            new Dropdown(dropdownButton);
          dropdownInstance.hide();
        }
      }
    }
  };

  
  return (
    <div className="btn-group w-100">
      <button
        className={`${className} text-truncate d-flex align-items-center gap-2`}
        type="button"
        id={id ? id : `dropdown-button-${name}`}
        data-bs-toggle="dropdown"
        aria-expanded="false"
        aria-haspopup="true"
        disabled={disabled}
        title={currentValue?.name || selected.selectedName || placeholder}
      >
        {prefix && <span className="text-muted">{prefix}</span>}
        <span className={`${name === 'pagesize' ? '' : 'text-truncate'}`} style={{ maxWidth: "calc(100% - 24px)" }}>
          {(currentValue
            ? currentValue.value
              ? selected.selectedName || currentValue.name
              : ""
            : selected.selectedName) || placeholder}
        </span>
        {displayLoader && (
          <div className="fs-7">
            <SmallLoader />
          </div>
        )}
      </button>
      <ul
        id={`custom-dropdown-${name}`}
        className="dropdown-menu w-100"
        aria-labelledby="dropdownMenuClickableInside"
        // onClick={(e) => {
        //   if ((e.target as HTMLLIElement).dataset.id) {
        //     const clickedItem = e.target as HTMLLIElement;
        //     const clickedItemId = clickedItem.dataset.id;
            
        //     // Find the index of the clicked item in the options array
        //     const clickedIndex = options.findIndex(opt => String(opt.value) === clickedItemId);
            
        //     // Update the selectedIndex state to match the clicked item
        //     setSelectedIndex(clickedIndex);
            
        //     setSelected({
        //       selectedName: clickedItem.textContent?.trim(),
        //       selectedValue: clickedItem.dataset.id,
        //     });
        //     setFieldValue &&
        //       setFieldValue(name, clickedItem.dataset.id);
        //     apiCallDispatch && apiCallDispatch(e);
        //   }
        // }}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {showSearch && (
          <div className="px-5 py-3">
            <input
              type="text"
              className="w-100 dropdown-search form-control"
              onChange={searchFun}
              value={search}
              placeholder="Search Here"
              id={`myInput-${name}`}
              ref={searchInputRef}
            />
          </div>
        )}
        <div className="mh-150px overflow-auto vertical-scroll">
          {options?.map((option: option, index: number) => {
            if (option.subDropDown) {
              return (
                <li key={index} className="p-3">
                  <span
                    className="dropdown-item dropdown-toggle ps-0 text-truncate"
                    id="dropdownMenuClickableInside1"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                    aria-expanded="false"
                    title={option.name}
                  >
                    {option.name}
                  </span>
                  <ul
                    className="dropdown-menu w-75"
                    aria-labelledby="dropdownMenuClickableInside1"
                  >
                    {option.subDropDown.map(
                      (subDropDownItem: subDropDown, index: number) => (
                        <li
                          key={index}
                          className={`dropdown-item position-relative cursor-pointer d-flex align-items-center justify-content-between pe-3
                            ${
                              (
                                currentValue
                                  ? currentValue.value == subDropDownItem.value
                                  : selected.selectedValue ==
                                    subDropDownItem.value
                              )
                                ? "dropdown-option-selected"
                                : ""
                            }`}
                          data-id={subDropDownItem.value}
                          title={subDropDownItem.name}
                        >
                          <span className="text-truncate" style={{ maxWidth: "calc(100% - 28px)" }}>{subDropDownItem.name}</span>
                          {(currentValue
                            ? currentValue.value == subDropDownItem.value
                            : selected.selectedValue == subDropDownItem.value) && (
                            <i className="bi bi-check2 ms-2" style={{ width: "20px" }}></i>
                          )}
                        </li>
                      )
                    )}
                  </ul>
                </li>
              );
            } else {
              return (
                <li
                  key={index}
                  // className={`dropdown-item d-flex gap-2 position-relative cursor-pointer
                  //       ${
                  //         (
                  //           currentValue
                  //             ? currentValue.value == option.value
                  //             : selected.selectedValue == option.value
                  //         )
                  //           ? "dropdown-option-selected"
                  //           : ""
                  //       }`}
                  className={`${getItemClassName(
                    index
                  )} d-flex align-items-center justify-content-between pe-3 position-relative cursor-pointer ${
                    (currentValue
                      ? currentValue.value == option.value
                      : selected.selectedValue == option.value)
                  }`}
                  data-id={option.value}
                  onClick={(e) => {
                    const nameSelector = name ? `#custom-dropdown-${CSS.escape(name)} .dropdown-item` : null;
                    const idSelector = id ? `#custom-dropdown-${CSS.escape(id)} .dropdown-item` : null;
                  
                    const selector = idSelector ? `${nameSelector}, ${idSelector}` : nameSelector;
                    const allItems = document.querySelectorAll(selector ? selector : `#custom-dropdown-${name} .dropdown-item`);
                    //const allItems = document.querySelectorAll(`#custom-dropdown-${name} .dropdown-item`);
                    allItems.forEach(item => item.classList.remove("dropdown-option-selected"));
                    
                    const clickedItem = e.currentTarget as HTMLLIElement;
                    clickedItem.classList.add("dropdown-option-selected");
                    
                    setSelectedIndex(index);

                    handleOptionClick(option.value, option.name);
                  }}
                  title={option.name}
                >
                  <span className="text-truncate" style={{ maxWidth: "calc(100% - 28px)" }}>{option.name}</span>
                </li>
              );
            }
          })}
        </div>
      </ul>
    </div>
  );
};

export default DropDown;
